import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, KeyRound, CalendarClock, LineChart,
  Copy, Eye, EyeOff, ArrowRight, Building2, FileImage,
} from "lucide-react";
import { Loading } from "@/pages/_shared";
import { Card } from "@/components/common/PageHeader";
import { toast } from "sonner";
import api from "@/lib/api";

function StatusBadge({ status }) {
  const map = {
    "Client Action Needed": "bg-amber-100 text-amber-700",
    "Working": "bg-blue-50 text-[#1a5fd0]",
    "Submitted": "bg-blue-50 text-[#1a5fd0]",
    "Not Started": "bg-slate-100 text-slate-500",
    "Purchase Needed": "bg-violet-50 text-violet-700",
    "Declined": "bg-red-50 text-red-600",
  };
  const cls = map[status] || "bg-emerald-50 text-emerald-600";
  return <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${cls}`}>{status}</span>;
}

function LoginRow({ item }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3" data-testid="login-row">
      <div className="min-w-0">
        <div className="font-head text-[14px] font-bold text-slate-900">{item.vendor_name}</div>
        <div className="truncate text-[12px] text-slate-500">{item.username}</div>
      </div>
      <div className="flex items-center gap-2">
        <code className="rounded bg-slate-100 px-2 py-1 font-mono text-[12px] text-slate-700">
          {show ? item.password : "•".repeat(12)}
        </code>
        <button onClick={() => setShow((v) => !v)} data-testid="toggle-password" className="text-slate-400 hover:text-slate-700">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
        <button onClick={() => { navigator.clipboard.writeText(item.password); toast.success("Password copied"); }}
          data-testid="copy-password" className="text-slate-400 hover:text-slate-700"><Copy size={15} /></button>
      </div>
    </div>
  );
}

export default function BusinessCredit() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/business-credit/me").then(({ data }) => setData(data)).catch(() => setData({ has_run: false })); }, []);

  const viewProof = async (id) => {
    try {
      const res = await api.get(`/business-credit/me/artifact/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not load the submission proof."); }
  };

  if (!data) return <Loading />;

  if (!data.has_run) {
    return (
      <div className="animate-fade-up" data-testid="business-credit-page">
        <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Business Credit</h1>
        <p className="mt-2 text-[15px] text-slate-500">Your dedicated Business Credit AI Employee sets up vendor accounts that build your business credit.</p>
        <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="bc-empty">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1a5fd0]/10 text-[#1a5fd0]"><ShieldCheck size={26} /></span>
          <div className="mt-4 font-head text-[17px] font-bold text-slate-900">Your setup hasn't started yet</div>
          <p className="mx-auto mt-1 max-w-md text-[14px] text-slate-500">
            Make sure your Business Profile is complete. Your MMT team then kicks off your vendor-account setup and it will appear here.
          </p>
          <button onClick={() => navigate("/business-identity")} data-testid="bc-complete-profile"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#1a5fd0] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:bg-[#1550b8]">
            <Building2 size={16} /> Complete Business Profile
          </button>
        </div>
      </div>
    );
  }

  const c = data.counts;
  return (
    <div className="animate-fade-up space-y-8" data-testid="business-credit-page">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Business Credit</h1>
        <p className="mt-2 text-[15px] text-slate-500">Set up and tracked for you by your Business Credit AI Employee.</p>
      </div>

      {/* Working → Action Needed → Completed */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-5" data-testid="stat-completed"><div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 size={18} /><span className="font-mono text-[10px] font-bold uppercase tracking-wider">Completed</span></div><div className="mt-2 font-head text-3xl font-extrabold text-slate-900">{c.completed}</div></Card>
        <Card className="p-5" data-testid="stat-pending"><div className="flex items-center gap-2 text-[#1a5fd0]"><LineChart size={18} /><span className="font-mono text-[10px] font-bold uppercase tracking-wider">Working</span></div><div className="mt-2 font-head text-3xl font-extrabold text-slate-900">{c.pending}</div></Card>
        <Card className="p-5" data-testid="stat-action"><div className="flex items-center gap-2 text-amber-600"><AlertTriangle size={18} /><span className="font-mono text-[10px] font-bold uppercase tracking-wider">Action Required</span></div><div className="mt-2 font-head text-3xl font-extrabold text-slate-900">{c.action}</div></Card>
      </div>

      {data.summary && (
        <Card className="border-[#1a5fd0]/20 bg-[#1a5fd0]/5 p-5" data-testid="bc-summary">
          <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#1a5fd0]">From your Business Credit AI Employee</div>
          <p className="mt-2 whitespace-pre-line text-[14px] leading-relaxed text-slate-700">{data.summary}</p>
        </Card>
      )}

      {/* Action Required */}
      {data.action.length > 0 && (
        <div data-testid="bc-action-section">
          <h2 className="mb-3 flex items-center gap-2 font-head text-lg font-extrabold text-slate-900"><AlertTriangle size={18} className="text-amber-600" /> Action Required</h2>
          <div className="space-y-3">
            {data.action.map((a) => (
              <Card key={a.id} className="border-amber-200 bg-amber-50/40 p-4" data-testid="action-card">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-head text-[15px] font-bold text-slate-900">{a.vendor_name}</div>
                  <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase text-amber-700">{a.action_required?.reason}</span>
                </div>
                <p className="mt-1.5 text-[14px] text-slate-700">{a.action_required?.what}</p>
                <p className="mt-1 text-[12px] text-slate-500">{a.action_required?.why}</p>
                {a.action_required?.cta && (
                  <button onClick={() => navigate(a.action_required.cta.to)} data-testid="action-cta"
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-4 py-2 font-head text-[13px] font-bold text-white hover:opacity-90">
                    {a.action_required.cta.label} <ArrowRight size={15} />
                  </button>
                )}
                {a.submission?.artifact_id && (
                  <div className="mt-2">
                    <span className="text-[12px] text-slate-500">We auto-filled everything else on the application — </span>
                    <button onClick={() => viewProof(a.submission.artifact_id)} data-testid="view-proof-btn"
                      className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#1a5fd0] hover:underline">
                      <FileImage size={13} /> view proof
                    </button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Completed accounts */}
      <div data-testid="bc-accounts-section">
        <h2 className="mb-3 font-head text-lg font-extrabold text-slate-900">Your accounts</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {data.accounts.map((a) => (
            <Card key={a.id} className="p-4" data-testid="account-card">
              <div className="flex items-center justify-between gap-2">
                <div className="font-head text-[15px] font-bold text-slate-900">{a.vendor_name}</div>
                <StatusBadge status={a.status} />
              </div>
              <div className="mt-1 text-[12px] text-slate-500">{a.category} · {a.terms}</div>
              {a.purchase?.required && <div className="mt-2 rounded-lg bg-violet-50 px-3 py-2 text-[12px] text-violet-700">{a.purchase.note}</div>}
              {a.reports_to?.length > 0 && <div className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-400">Reports to {a.reports_to.join(", ")}</div>}
              {a.submission?.confirmation && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-[12px] font-semibold text-emerald-700" data-testid="submission-confirmation">
                  <CheckCircle2 size={14} /> Submitted · Confirmation {a.submission.confirmation}
                </div>
              )}
              {a.submission?.artifact_id && (
                <button onClick={() => viewProof(a.submission.artifact_id)} data-testid="view-proof-btn"
                  className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#1a5fd0] hover:underline">
                  <FileImage size={13} /> View submission proof
                </button>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* My Logins */}
      {data.logins.length > 0 && (
        <div data-testid="bc-logins-section">
          <h2 className="mb-3 flex items-center gap-2 font-head text-lg font-extrabold text-slate-900"><KeyRound size={18} className="text-[#1a5fd0]" /> My Logins</h2>
          <div className="space-y-2">{data.logins.map((l) => <LoginRow key={l.vendor_name} item={l} />)}</div>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        {/* Payments Due */}
        {data.payments.length > 0 && (
          <div data-testid="bc-payments-section">
            <h2 className="mb-3 flex items-center gap-2 font-head text-lg font-extrabold text-slate-900"><CalendarClock size={18} className="text-slate-700" /> Payments Due</h2>
            <div className="space-y-2">
              {data.payments.map((p) => (
                <div key={p.vendor_name} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                  <span className="text-[14px] font-semibold text-slate-800">{p.vendor_name}</span>
                  <span className="font-mono text-[12px] text-slate-500">{p.payment_due}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Reporting Status */}
        {data.reporting.length > 0 && (
          <div data-testid="bc-reporting-section">
            <h2 className="mb-3 flex items-center gap-2 font-head text-lg font-extrabold text-slate-900"><LineChart size={18} className="text-slate-700" /> Reporting Status</h2>
            <div className="space-y-2">
              {data.reporting.map((r) => (
                <div key={r.vendor_name} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                  <span className="text-[14px] font-semibold text-slate-800">{r.vendor_name}</span>
                  <span className={`font-mono text-[11px] font-bold uppercase ${r.reporting_status === "Reporting Confirmed" ? "text-emerald-600" : "text-slate-400"}`}>{r.reporting_status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="pt-2 text-center text-[12px] text-slate-400">Vendor accounts are set up in a clearly-labeled SANDBOX environment until live vendor connections are authorized.</p>
    </div>
  );
}
